(() => {
  'use strict';

  const ROOT = window.Shopify?.routes?.root || '/';

  let cartBusy = false;
  let drawerRefreshBusy = false;
  let eligibilityBusy = false;
  let eligibilityTimer = null;
  let lastHandledAt = 0;
  let pointerStart = null;

  /* ======================================================
     BASIC HELPERS
  ====================================================== */

  function isGiftItem(item) {
    return String(item?.properties?._rk_gift || '') === 'true';
  }

  function getPaidItemCount(cart) {
    return cart.items.reduce((total, item) => {
      if (isGiftItem(item)) {
        return total;
      }

      return total + Number(item.quantity || 0);
    }, 0);
  }

  function getRequiredGiftLevel(paidItemCount) {
    if (paidItemCount >= 3) {
      return 'kurti';
    }

    if (paidItemCount >= 2) {
      return 'tunic';
    }

    return '';
  }

  function getGiftLines(cart) {
    return cart.items.filter(isGiftItem);
  }

  function getGiftLevel(item) {
    return String(
      item?.properties?._rk_gift_level || ''
    );
  }

  function parseProducts(selector) {
    const jsonElement = selector?.querySelector(
      '[data-rk-products-json]'
    );

    if (!jsonElement) {
      return [];
    }

    try {
      return JSON.parse(
        jsonElement.textContent.trim()
      );
    } catch (error) {
      console.error(
        '[Rangkari Gift Selector] Invalid product JSON:',
        error
      );

      return [];
    }
  }

  function getSelectorState(selector) {
    return {
      productId: Number(
        selector?.dataset.selectedProductId || 0
      ),
      variantId: Number(
        selector?.dataset.selectedVariantId || 0
      )
    };
  }

  function setSelectorState(selector, nextState) {
    if (!selector) return;

    if (
      Object.prototype.hasOwnProperty.call(
        nextState,
        'productId'
      )
    ) {
      selector.dataset.selectedProductId = String(
        nextState.productId || 0
      );
    }

    if (
      Object.prototype.hasOwnProperty.call(
        nextState,
        'variantId'
      )
    ) {
      selector.dataset.selectedVariantId = String(
        nextState.variantId || 0
      );
    }
  }

  function findProduct(products, productId) {
    return (
      products.find(
        product =>
          Number(product.id) === Number(productId)
      ) || null
    );
  }

  function findVariant(product, variantId) {
    if (!product) return null;

    return (
      product.variants.find(
        variant =>
          Number(variant.id) === Number(variantId)
      ) || null
    );
  }

  /* ======================================================
     SHOPIFY AJAX API
  ====================================================== */

  async function api(path, options = {}) {
    const response = await fetch(
      `${ROOT}${path.replace(/^\//, '')}`,
      {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...(options.headers || {})
        },
        ...options
      }
    );

    const data = await response
      .json()
      .catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        data.description ||
        data.message ||
        'Unable to update the cart.'
      );
    }

    return data;
  }

  async function getCart() {
    return api('cart.js', {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      }
    });
  }

  /*
   * Removes all gift lines in one Shopify request.
   * This prevents an old Tunic gift from combining with
   * a newly selected Kurti Set gift.
   */
  async function removeAllGiftLines(cart = null) {
    const currentCart = cart || await getCart();
    const giftLines = getGiftLines(currentCart);

    if (!giftLines.length) {
      return false;
    }

    const updates = {};

    giftLines.forEach(item => {
      updates[item.key] = 0;
    });

    await api('cart/update.js', {
      method: 'POST',
      body: JSON.stringify({
        updates
      })
    });

    return true;
  }

  /*
   * Final safety cleanup:
   * - Keeps only the correct selected gift
   * - Forces its quantity to exactly 1
   * - Removes all old or duplicate gift lines
   */
  async function normalizeGiftLines(
    selectedVariantId,
    selectedGiftLevel
  ) {
    const cart = await getCart();
    const giftLines = getGiftLines(cart);

    if (!giftLines.length) {
      return cart;
    }

    const updates = {};
    let selectedGiftKept = false;

    giftLines.forEach(item => {
      const isSelectedVariant =
        Number(item.variant_id) ===
        Number(selectedVariantId);

      const isSelectedLevel =
        getGiftLevel(item) ===
        selectedGiftLevel;

      const shouldKeep =
        isSelectedVariant &&
        isSelectedLevel &&
        !selectedGiftKept;

      if (shouldKeep) {
        updates[item.key] = 1;
        selectedGiftKept = true;
      } else {
        updates[item.key] = 0;
      }
    });

    await api('cart/update.js', {
      method: 'POST',
      body: JSON.stringify({
        updates
      })
    });

    return getCart();
  }

  /* ======================================================
     SELECTOR RENDERING
  ====================================================== */

  function renderProductCards(
    selector,
    selectedProductId
  ) {
    selector
      .querySelectorAll('[data-rk-gift-product]')
      .forEach(card => {
        const selected =
          Number(card.dataset.productId) ===
          Number(selectedProductId);

        card.classList.toggle(
          'is-selected',
          selected
        );

        card.setAttribute(
          'aria-pressed',
          selected ? 'true' : 'false'
        );
      });
  }

  function renderSizeButtons(
    selector,
    selectedProduct,
    selectedVariantId
  ) {
    const sizeList = selector.querySelector(
      '[data-rk-size-list]'
    );

    if (!sizeList) return;

    sizeList.innerHTML = '';

    if (!selectedProduct) {
      sizeList.innerHTML = `
        <span class="rk-gift-size-placeholder">
          Select a gift product first.
        </span>
      `;

      return;
    }

    selectedProduct.variants.forEach(variant => {
      const button = document.createElement('button');

      const selected =
        Number(variant.id) ===
        Number(selectedVariantId);

      button.type = 'button';

      button.className =
        `rk-gift-size${
          selected ? ' is-selected' : ''
        }`;

      button.dataset.rkGiftSize = '';
      button.dataset.variantId = String(
        variant.id
      );

      button.textContent =
        variant.size || variant.title;

      button.setAttribute(
        'aria-pressed',
        selected ? 'true' : 'false'
      );

      sizeList.appendChild(button);
    });
  }

  function renderSelectedSummary(
    selector,
    selectedProduct,
    selectedVariant
  ) {
    const summary = selector.querySelector(
      '[data-rk-selected-summary]'
    );

    if (!summary) return;

    const hasSelection = Boolean(
      selectedProduct && selectedVariant
    );

    summary.classList.toggle(
      'is-hidden',
      !hasSelection
    );

    if (!hasSelection) {
      return;
    }

    const titleElement = summary.querySelector(
      '[data-rk-selected-title]'
    );

    const sizeElement = summary.querySelector(
      '[data-rk-selected-size]'
    );

    const imageElement = summary.querySelector(
      '[data-rk-selected-image]'
    );

    if (titleElement) {
      titleElement.textContent =
        selectedProduct.title;
    }

    if (sizeElement) {
      sizeElement.textContent =
        `Size: ${
          selectedVariant.size ||
          selectedVariant.title
        }`;
    }

    if (imageElement) {
      imageElement.src =
        selectedProduct.image || '';

      imageElement.alt =
        selectedProduct.title ||
        'Selected complimentary gift';
    }
  }

  function renderFooter(
    selector,
    selectedProduct,
    selectedVariant
  ) {
    const confirmButton = selector.querySelector(
      '[data-rk-confirm-gift]'
    );

    const messageElement = selector.querySelector(
      '[data-rk-message]'
    );

    if (confirmButton) {
      confirmButton.disabled =
        !selectedProduct ||
        !selectedVariant ||
        cartBusy;
    }

    if (!messageElement) return;

    if (!selectedProduct) {
      messageElement.textContent =
        'Select your gift product first.';
    } else if (!selectedVariant) {
      messageElement.textContent =
        'Now select the size for your chosen gift.';
    } else {
      messageElement.textContent =
        'Gift and size selected. Tap “Confirm Free Gift”.';
    }
  }

  function renderSelector(selector) {
    if (!selector) return;

    const products = parseProducts(selector);
    const state = getSelectorState(selector);

    const selectedProduct = findProduct(
      products,
      state.productId
    );

    const selectedVariant = findVariant(
      selectedProduct,
      state.variantId
    );

    renderProductCards(
      selector,
      state.productId
    );

    renderSizeButtons(
      selector,
      selectedProduct,
      state.variantId
    );

    renderSelectedSummary(
      selector,
      selectedProduct,
      selectedVariant
    );

    renderFooter(
      selector,
      selectedProduct,
      selectedVariant
    );
  }

  /* ======================================================
     CART COUNT
  ====================================================== */

  function updateVisibleCartCounts(cart) {
    /*
     * Only paid products are counted.
     * Complimentary gift quantity is excluded.
     */
    const paidItemCount = getPaidItemCount(cart);

    document
      .querySelectorAll('[data-cart-count]')
      .forEach(element => {
        element.textContent = paidItemCount;
      });

    document
      .querySelectorAll(
        '.cart-count-bubble .text-count'
      )
      .forEach(element => {
        element.textContent = paidItemCount;
      });

    document
      .querySelectorAll('[data-cart-text]')
      .forEach(element => {
        element.textContent =
          paidItemCount === 1
            ? 'item'
            : 'items';
      });

    return paidItemCount;
  }

  /* ======================================================
     CART DRAWER REFRESH
  ====================================================== */

  function keepDrawerOpen(drawer) {
    if (!drawer) return;

    drawer.classList.add(
      'is-open',
      'is-show',
      'active'
    );

    drawer.setAttribute(
      'aria-hidden',
      'false'
    );

    document.documentElement.classList.add(
      'cart-sidebar-show'
    );

    document.body.classList.add(
      'cart-sidebar-show'
    );

    const overlay = document.querySelector(
      '.background-overlay'
    );

    if (overlay) {
      overlay.classList.add('is-show');
    }
  }

  async function refreshCartDrawer() {
    if (drawerRefreshBusy) return;

    drawerRefreshBusy = true;

    try {
      const drawer = document.querySelector(
        '#halo-cart-sidebar'
      );

      const currentWrapper =
        drawer?.querySelector(
          '.previewCart-wrapper'
        );

      if (!drawer || !currentWrapper) {
        return;
      }

      const response = await fetch(
        `${ROOT}cart?view=ajax_side_cart&_=${Date.now()}`,
        {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: {
            Accept: 'text/html',
            'X-Requested-With':
              'XMLHttpRequest'
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          'Unable to refresh the cart drawer.'
        );
      }

      const html = await response.text();

      const temporaryContainer =
        document.createElement('div');

      temporaryContainer.innerHTML =
        html.trim();

      const returnedWrapper =
        temporaryContainer.querySelector(
          '.previewCart-wrapper'
        );

      if (returnedWrapper) {
        currentWrapper.innerHTML =
          returnedWrapper.innerHTML;
      } else {
        currentWrapper.innerHTML = html;
      }

      const updatedCart = await getCart();

      updateVisibleCartCounts(updatedCart);
      initializeSelectors(currentWrapper);
      keepDrawerOpen(drawer);

      document.dispatchEvent(
        new CustomEvent(
          'rk:cart-drawer-refreshed',
          {
            detail: {
              cart: updatedCart
            }
          }
        )
      );
    } catch (error) {
      console.error(
        '[Rangkari Gift Selector] Drawer refresh error:',
        error
      );
    } finally {
      drawerRefreshBusy = false;
    }
  }

  /* ======================================================
     GIFT ELIGIBILITY RECONCILIATION
  ====================================================== */

  async function reconcileGiftEligibility(
    options = {}
  ) {
    if (
      eligibilityBusy ||
      cartBusy
    ) {
      return;
    }

    const shouldRefresh =
      options.refresh !== false;

    eligibilityBusy = true;

    try {
      let cart = await getCart();

      const paidItemCount =
        getPaidItemCount(cart);

      const requiredGiftLevel =
        getRequiredGiftLevel(
          paidItemCount
        );

      const giftLines =
        getGiftLines(cart);

      let cartChanged = false;

      /*
       * Fewer than two paid products:
       * no gift is allowed.
       */
      if (!requiredGiftLevel) {
        if (giftLines.length) {
          await removeAllGiftLines(cart);
          cartChanged = true;
        }
      } else {
        const updates = {};
        let correctGiftKept = false;

        giftLines.forEach(item => {
          const itemLevel =
            getGiftLevel(item);

          const correctLevel =
            itemLevel === requiredGiftLevel;

          /*
           * Keep only one gift of the correct level.
           */
          if (
            correctLevel &&
            !correctGiftKept
          ) {
            updates[item.key] = 1;
            correctGiftKept = true;

            if (
              Number(item.quantity) !== 1
            ) {
              cartChanged = true;
            }
          } else {
            updates[item.key] = 0;
            cartChanged = true;
          }
        });

        if (
          giftLines.length &&
          cartChanged
        ) {
          await api('cart/update.js', {
            method: 'POST',
            body: JSON.stringify({
              updates
            })
          });
        }
      }

      if (cartChanged) {
        cart = await getCart();
      }

      updateVisibleCartCounts(cart);

      if (
        shouldRefresh &&
        cartChanged
      ) {
        await refreshCartDrawer();
      }
    } catch (error) {
      console.error(
        '[Rangkari Gift Selector] Eligibility check error:',
        error
      );
    } finally {
      eligibilityBusy = false;
    }
  }

  function scheduleEligibilityCheck(
    delay = 700
  ) {
    window.clearTimeout(
      eligibilityTimer
    );

    eligibilityTimer =
      window.setTimeout(
        () => {
          reconcileGiftEligibility({
            refresh: true
          });
        },
        delay
      );
  }

  function scheduleMultipleEligibilityChecks() {
    scheduleEligibilityCheck(500);

    window.setTimeout(() => {
      scheduleEligibilityCheck(200);
    }, 1100);

    window.setTimeout(() => {
      scheduleEligibilityCheck(200);
    }, 1900);
  }

  /* ======================================================
     CONFIRM OR CHANGE GIFT
  ====================================================== */

  async function confirmGift(selector) {
    if (
      cartBusy ||
      !selector
    ) {
      return;
    }

    const products =
      parseProducts(selector);

    const state =
      getSelectorState(selector);

    const selectedProduct =
      findProduct(
        products,
        state.productId
      );

    const selectedVariant =
      findVariant(
        selectedProduct,
        state.variantId
      );

    const selectedGiftLevel =
      selector.dataset.level;

    if (
      !selectedProduct ||
      !selectedVariant ||
      !selectedGiftLevel
    ) {
      renderSelector(selector);
      return;
    }

    cartBusy = true;

    selector.classList.add(
      'is-loading'
    );

    const confirmButton =
      selector.querySelector(
        '[data-rk-confirm-gift]'
      );

    const messageElement =
      selector.querySelector(
        '[data-rk-message]'
      );

    const originalButtonText =
      confirmButton?.textContent?.trim() ||
      'Confirm Free Gift';

    if (confirmButton) {
      confirmButton.disabled = true;
      confirmButton.textContent =
        'Adding Gift...';
    }

    try {
      const currentCart =
        await getCart();

      const paidItemCount =
        getPaidItemCount(currentCart);

      const requiredGiftLevel =
        getRequiredGiftLevel(
          paidItemCount
        );

      /*
       * Prevent adding a Tunic when the cart now
       * qualifies for a Kurti Set, or vice versa.
       */
      if (
        !requiredGiftLevel ||
        requiredGiftLevel !==
          selectedGiftLevel
      ) {
        throw new Error(
          'Your gift eligibility changed. Please select the correct complimentary gift.'
        );
      }

      /*
       * Remove every previous gift first.
       */
      await removeAllGiftLines(
        currentCart
      );

      /*
       * Confirm that Shopify removed old gifts
       * before adding the new gift.
       */
      const cartAfterRemoval =
        await getCart();

      if (
        getGiftLines(cartAfterRemoval)
          .length
      ) {
        await removeAllGiftLines(
          cartAfterRemoval
        );
      }

      /*
       * Add exactly one new gift.
       *
       * _rk_gift_instance ensures the old Tunic gift
       * cannot merge with the new Kurti Set gift.
       */
      await api('cart/add.js', {
        method: 'POST',
        body: JSON.stringify({
          items: [
            {
              id: Number(
                selectedVariant.id
              ),
              quantity: 1,
              properties: {
                _rk_gift: 'true',

                _rk_gift_level:
                  selectedGiftLevel,

                _rk_gift_title:
                  selectedProduct.title,

                _rk_gift_size:
                  selectedVariant.size ||
                  selectedVariant.title,

                _rk_gift_instance:
                  `${selectedGiftLevel}-${selectedVariant.id}-${Date.now()}`
              }
            }
          ]
        })
      });

      /*
       * Force the final gift quantity to one and
       * remove accidental duplicate gift lines.
       */
      const normalizedCart =
        await normalizeGiftLines(
          selectedVariant.id,
          selectedGiftLevel
        );

      updateVisibleCartCounts(
        normalizedCart
      );

      await refreshCartDrawer();
    } catch (error) {
      console.error(
        '[Rangkari Gift Selector]',
        error
      );

      if (messageElement) {
        messageElement.textContent =
          error.message;
      }

      if (confirmButton) {
        confirmButton.disabled = false;

        confirmButton.textContent =
          originalButtonText;
      }
    } finally {
      cartBusy = false;

      selector.classList.remove(
        'is-loading'
      );
    }
  }

  /* ======================================================
     PRODUCT AND SIZE SELECTION
  ====================================================== */

  function selectProduct(productCard) {
    const selector =
      productCard?.closest(
        '[data-rk-gift-selector]'
      );

    if (!selector) return;

    const productId = Number(
      productCard.dataset.productId || 0
    );

    if (!productId) return;

    setSelectorState(selector, {
      productId,
      variantId: 0
    });

    renderSelector(selector);

    window.requestAnimationFrame(() => {
      selector
        .querySelector(
          '[data-rk-size-step]'
        )
        ?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
    });
  }

  function selectSize(sizeButton) {
    const selector =
      sizeButton?.closest(
        '[data-rk-gift-selector]'
      );

    if (!selector) return;

    const variantId = Number(
      sizeButton.dataset.variantId || 0
    );

    if (!variantId) return;

    setSelectorState(selector, {
      variantId
    });

    renderSelector(selector);
  }

  /* ======================================================
     CLICK AND MOBILE TAP HANDLING
  ====================================================== */

  function handleGiftAction(
    target,
    event
  ) {
    const now = Date.now();

    if (
      now - lastHandledAt < 250
    ) {
      return false;
    }

    const productCard =
      target.closest?.(
        '[data-rk-gift-product]'
      );

    if (productCard) {
      lastHandledAt = now;

      event?.preventDefault();
      event?.stopPropagation();

      selectProduct(productCard);

      return true;
    }

    const sizeButton =
      target.closest?.(
        '[data-rk-gift-size]'
      );

    if (sizeButton) {
      lastHandledAt = now;

      event?.preventDefault();
      event?.stopPropagation();

      selectSize(sizeButton);

      return true;
    }

    const confirmButton =
      target.closest?.(
        '[data-rk-confirm-gift]'
      );

    if (confirmButton) {
      lastHandledAt = now;

      event?.preventDefault();
      event?.stopPropagation();

      const selector =
        confirmButton.closest(
          '[data-rk-gift-selector]'
        );

      if (selector) {
        confirmGift(selector);
      }

      return true;
    }

    return false;
  }

  function isCartQuantityAction(target) {
    return Boolean(
      target.closest?.(
        [
          '[data-cart-remove]',
          '[data-minus-quantity-cart]',
          '[data-plus-quantity-cart]',
          '[data-cart-quantity]',
          'cart-update-quantity',
          '.previewCartItem-remove',
          '.previewCartItem-qty'
        ].join(',')
      )
    );
  }

  document.addEventListener(
    'click',
    event => {
      const giftActionHandled =
        handleGiftAction(
          event.target,
          event
        );

      if (giftActionHandled) {
        return;
      }

      /*
       * Ella updates quantities asynchronously.
       * Check several times after the action finishes.
       */
      if (
        isCartQuantityAction(
          event.target
        )
      ) {
        scheduleMultipleEligibilityChecks();
      }
    },
    true
  );

  document.addEventListener(
    'change',
    event => {
      if (
        event.target.matches?.(
          [
            '[data-cart-quantity]',
            'input[name="quantity"]'
          ].join(',')
        )
      ) {
        scheduleMultipleEligibilityChecks();
      }
    },
    true
  );

  document.addEventListener(
    'pointerdown',
    event => {
      if (
        !event.target.closest?.(
          '[data-rk-gift-selector]'
        )
      ) {
        return;
      }

      pointerStart = {
        x: event.clientX,
        y: event.clientY,
        time: Date.now(),
        pointerId: event.pointerId
      };
    },
    true
  );

  document.addEventListener(
    'pointerup',
    event => {
      if (
        !pointerStart ||
        pointerStart.pointerId !==
          event.pointerId
      ) {
        return;
      }

      const deltaX = Math.abs(
        event.clientX -
          pointerStart.x
      );

      const deltaY = Math.abs(
        event.clientY -
          pointerStart.y
      );

      const elapsed =
        Date.now() -
        pointerStart.time;

      pointerStart = null;

      const isTap =
        deltaX < 12 &&
        deltaY < 12 &&
        elapsed < 650;

      if (!isTap) {
        return;
      }

      handleGiftAction(
        event.target,
        event
      );
    },
    true
  );

  /* ======================================================
     INITIALIZATION
  ====================================================== */

  function initializeSelectors(
    root = document
  ) {
    if (
      root.matches?.(
        '[data-rk-gift-selector]'
      )
    ) {
      renderSelector(root);
    }

    root
      .querySelectorAll?.(
        '[data-rk-gift-selector]'
      )
      .forEach(renderSelector);
  }

  document.addEventListener(
    'DOMContentLoaded',
    async () => {
      initializeSelectors();

      await reconcileGiftEligibility({
        refresh: false
      });
    }
  );

  document.addEventListener(
    'cart:updated',
    () => {
      initializeSelectors();
      scheduleEligibilityCheck(400);
    }
  );

  document.addEventListener(
    'cart:refresh',
    () => {
      scheduleEligibilityCheck(400);
    }
  );

  document.addEventListener(
    'rk:cart-drawer-refreshed',
    () => {
      initializeSelectors();
    }
  );

  /*
   * Reinitialize after Ella replaces the cart drawer HTML.
   */
  const observer =
    new MutationObserver(
      mutations => {
        for (
          const mutation of mutations
        ) {
          for (
            const node of
              mutation.addedNodes
          ) {
            if (
              !(node instanceof Element)
            ) {
              continue;
            }

            if (
              node.matches?.(
                '[data-rk-gift-selector]'
              ) ||
              node.querySelector?.(
                '[data-rk-gift-selector]'
              )
            ) {
              initializeSelectors(node);
            }
          }
        }
      }
    );

  observer.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );
})();